import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CreditCard,
  Headphones,
  Inbox,
  Megaphone,
  MessageCircle,
  Package,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  fetchAdminNotifications,
  fetchAdminNotificationsUnreadCount,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  type AdminNotificationCategory,
  type AdminNotificationItem,
} from '@/api/notifications'
import { getEcho } from '@/lib/echo'
import { useT } from '@/lib/i18n/AdminI18nProvider'
import { cn } from '@/lib/utils'

type CategoryDef = {
  id: AdminNotificationCategory
  icon: LucideIcon
  labelKey: string
}

const CATEGORIES: CategoryDef[] = [
  { id: 'orders', icon: Package, labelKey: 'shell.notifCat.orders' },
  { id: 'payments', icon: CreditCard, labelKey: 'shell.notifCat.payments' },
  { id: 'tickets', icon: Headphones, labelKey: 'shell.notifCat.tickets' },
  { id: 'messages', icon: Inbox, labelKey: 'shell.notifCat.messages' },
  { id: 'livechat', icon: MessageCircle, labelKey: 'shell.notifCat.livechat' },
  { id: 'adverts', icon: Megaphone, labelKey: 'shell.notifCat.adverts' },
  { id: 'wallet', icon: Wallet, labelKey: 'shell.notifCat.wallet' },
]

function formatNotifAge(
  iso: string | null,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return t('shell.justNow')
  if (mins < 60) return t('shell.minutesAgo', { count: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('shell.hoursAgo', { count: hours })
  return t('shell.daysAgo', { count: Math.floor(hours / 24) })
}

export function AdminNotificationBells() {
  const t = useT()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const rootRef = useRef<HTMLDivElement>(null)
  const [openCategory, setOpenCategory] = useState<AdminNotificationCategory | null>(null)

  const countsQ = useQuery({
    queryKey: ['admin-notifications', 'counts'],
    queryFn: fetchAdminNotificationsUnreadCount,
    staleTime: 15_000,
    // Reverb anlık yeniler; polling yalnızca yedek
    refetchInterval: 60_000,
  })

  const listQ = useQuery({
    queryKey: ['admin-notifications', 'list', openCategory],
    queryFn: () =>
      fetchAdminNotifications({
        limit: 40,
        category: openCategory ?? undefined,
      }),
    enabled: openCategory != null,
    staleTime: 10_000,
  })

  const markReadMut = useMutation({
    mutationFn: (id: number) => markAdminNotificationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notifications'] })
    },
  })

  const markAllMut = useMutation({
    mutationFn: (category: AdminNotificationCategory) => markAllAdminNotificationsRead(category),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notifications'] })
    },
  })

  useEffect(() => {
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: ['admin-notifications'] })
    }

    try {
      getEcho()
        .private('admin.notifications')
        .listen('.notification.created', refresh)
        .listen('.notification.dismissed', refresh)
    } catch {
      /* Reverb kapalıysa polling devam eder */
    }

    return () => {
      try {
        getEcho().leave('private-admin.notifications')
      } catch {
        /* noop */
      }
    }
  }, [qc])

  useEffect(() => {
    if (!openCategory) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpenCategory(null)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [openCategory])

  const counts = countsQ.data?.counts_by_category ?? {}
  const items = listQ.data?.items ?? []
  const openUnread = openCategory ? (counts[openCategory] ?? 0) : 0
  const openDef = CATEGORIES.find((c) => c.id === openCategory)

  async function openItem(item: AdminNotificationItem) {
    setOpenCategory(null)
    if (item.unread) {
      try {
        await markReadMut.mutateAsync(item.id)
      } catch {
        /* navigate anyway */
      }
    }
    if (item.href) navigate(item.href)
  }

  return (
    <div className="relative flex items-center gap-0.5" ref={rootRef}>
      {CATEGORIES.map((cat) => {
        const count = counts[cat.id] ?? 0
        const Icon = cat.icon
        const isOpen = openCategory === cat.id

        return (
          <button
            key={cat.id}
            type="button"
            className={cn(
              'relative rounded-xl p-2 text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
              isOpen && 'bg-muted text-foreground',
              count > 0 && 'text-foreground',
            )}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label={`${t(cat.labelKey)}${count > 0 ? ` (${count})` : ''}`}
            title={t(cat.labelKey)}
            onClick={() => setOpenCategory((cur) => (cur === cat.id ? null : cat.id))}
          >
            <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
            {count > 0 ? (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-mainColor px-1 text-[9px] font-bold text-white ring-2 ring-card dark:ring-secondaryDark">
                {count > 9 ? '9+' : count}
              </span>
            ) : null}
          </button>
        )
      })}

      {openCategory && openDef ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 origin-top-right animate-in fade-in zoom-in-95 slide-in-from-top-2 rounded-2xl border border-border bg-card py-3 shadow-strong duration-200 dark:border-gray-700 dark:bg-secondaryDark">
          <div className="flex items-start justify-between gap-2 border-b border-border px-4 pb-3 dark:border-gray-700">
            <div>
              <p className="text-sm font-semibold text-foreground">{t(openDef.labelKey)}</p>
              <p className="text-xs text-muted-foreground">
                {openUnread > 0
                  ? t('shell.pendingCount', { count: openUnread })
                  : t('shell.noPending')}
              </p>
            </div>
            {openUnread > 0 ? (
              <button
                type="button"
                className="shrink-0 text-[11px] font-medium text-mainColor hover:underline disabled:opacity-50"
                disabled={markAllMut.isPending}
                onClick={() => markAllMut.mutate(openCategory)}
              >
                {t('shell.markAllRead')}
              </button>
            ) : null}
          </div>

          {listQ.isLoading ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">{t('common.loading')}</p>
          ) : items.length ? (
            <ul className="max-h-72 overflow-y-auto py-1">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={cn(
                      'block w-full px-4 py-2.5 text-left transition-colors hover:bg-muted',
                      item.unread && 'bg-mainColor/5',
                    )}
                    onClick={() => void openItem(item)}
                  >
                    <div className="flex items-start gap-2">
                      {item.unread ? (
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-mainColor" aria-hidden />
                      ) : (
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0" aria-hidden />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm text-foreground', item.unread && 'font-semibold')}>
                          {item.title}
                        </p>
                        {item.body ? (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.body}</p>
                        ) : null}
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {formatNotifAge(item.created_at, t)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              {t('shell.emptyNotifications')}
            </p>
          )}
        </div>
      ) : null}
    </div>
  )
}
